import { useState, useEffect, createContext, useContext } from 'react';
import { firestoreService } from '../services/firestoreService';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../config/firebase';

const ChapterContext = createContext({});

export const useChapters = () => {
  const context = useContext(ChapterContext);
  if (!context) {
    throw new Error('useChapters must be used within a ChapterProvider');
  }
  return context;
};

export const ChapterProvider = ({ children }) => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real-time chapter loading with Firestore listeners
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    console.log('🔥 Setting up real-time chapter listener...');
    
    const q = query(collection(db, 'chapters'), orderBy('order', 'asc'));
    
    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const chaptersData = [];
        querySnapshot.forEach((doc) => {
          chaptersData.push({ id: doc.id, ...doc.data() });
        });
        
        setChapters(chaptersData);
        setLoading(false);
        console.log('📚 Chapters updated in real-time:', chaptersData.length);
      },
      (error) => {
        console.error('❌ Real-time listener error:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      console.log('🔥 Cleaning up chapter listener');
      unsubscribe();
    };
  }, []);

  const loadChapters = async () => {
    // This method is now primarily for manual refresh if needed
    console.log('🔄 Manual chapter reload requested');
  };

  const createChapter = async (chapterData) => {
    try {
      const result = await firestoreService.createChapter(chapterData);
      // No need to manually refresh - real-time listener handles it
      console.log('✅ Chapter created, real-time listener will update UI');
      return result;
    } catch (error) {
      console.error('Error creating chapter:', error);
      return { success: false, error: error.message };
    }
  };

  const updateChapter = async (chapterId, updates) => {
    try {
      const result = await firestoreService.updateChapter(chapterId, updates);
      // No need to manually refresh - real-time listener handles it
      console.log('✅ Chapter updated, real-time listener will update UI');
      return result;
    } catch (error) {
      console.error('Error updating chapter:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteChapter = async (chapterId) => {
    try {
      const result = await firestoreService.deleteChapter(chapterId);
      // No need to manually refresh - real-time listener handles it
      console.log('✅ Chapter deleted, real-time listener will update UI');
      return result;
    } catch (error) {
      console.error('Error deleting chapter:', error);
      return { success: false, error: error.message };
    }
  };

  const getVisibleChapters = (accessLevel) => {
    switch (accessLevel) {
      case 'free':
        return chapters.filter(chapter => 
          chapter.type === 'free' || chapter.type === 'preview'
        );
      case 'paid':
        return chapters.filter(chapter => 
          chapter.isPaidContent || chapter.type === 'full'
        );
      case 'all':
        return chapters;
      default:
        return chapters;
    }
  };

  const value = {
    chapters,
    loading,
    error,
    loadChapters,
    createChapter,
    updateChapter,
    deleteChapter,
    getVisibleChapters
  };

  return (
    <ChapterContext.Provider value={value}>
      {children}
    </ChapterContext.Provider>
  );
};

export { ChapterContext };
